/**
 * O aviso dos Primeiros Passos na página inicial (spec #387, seção 5.3).
 *
 * Aparece quando existe um passo pendente que a pessoa consegue dar e leva à página que o
 * explica. O passo vem da mesma função da página, sobre o mesmo payload, então os dois nunca
 * discordam. Dispensar vale na sessão do navegador e por contexto: quem troca de empresa vê o
 * aviso daquela empresa de novo.
 */

import React, { useCallback, useState } from 'react';
import { Alert, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { RouteActionButton } from '../../../components';
import { semPorta } from '../../../authorization/pode';
import { useOnboarding } from '../../../hooks/useOnboarding';
import { useChaveDoContexto } from '../../../stores/chaveDoContexto';
import { useManifesto } from '../../../stores/manifestoStore';
import { progresso, proximoPasso } from '../../onboarding/proximoPasso';

const PREFIXO_DA_CHAVE = 'primeiros-passos:aviso-dispensado:';

const lerDispensa = (chave: string): boolean => {
  try {
    return sessionStorage.getItem(chave) === '1';
  } catch {
    return false;
  }
};

const gravarDispensa = (chave: string): void => {
  try {
    sessionStorage.setItem(chave, '1');
  } catch {
    // Sem armazenamento da sessão o aviso só some até o próximo carregamento.
  }
};

export interface OnboardingBannerProps {
  /** A página já mostra o seu "Por onde começar": dois próximos passos na mesma dobra confundem. */
  suprimido?: boolean;
}

export const OnboardingBanner: React.FC<OnboardingBannerProps> = ({ suprimido = false }) => {
  const onboarding = useOnboarding();
  const { manifesto } = useManifesto();
  const contexto = useChaveDoContexto();
  const chave = `${PREFIXO_DA_CHAVE}${contexto ?? ''}`;

  const [dispensados, setDispensados] = useState<readonly string[]>([]);
  const dispensar = useCallback(() => {
    gravarDispensa(chave);
    setDispensados((atuais) => [...atuais, chave]);
  }, [chave]);

  if (suprimido || onboarding.estado !== 'pronto' || !contexto) return null;
  if (dispensados.includes(chave) || lerDispensa(chave)) return null;

  const passo = proximoPasso(onboarding.status, manifesto);
  if (!passo) return null;

  const { feitos, total } = progresso(onboarding.status);
  const titulo = passo.texto.proximo?.titulo(undefined) ?? passo.acao.rotulo;

  return (
    <Alert
      severity="info"
      icon={false}
      role="note"
      sx={{ mb: 3, alignItems: 'center' }}
      action={
        <>
          <RouteActionButton
            para="/primeiros-passos"
            exige={semPorta('cada item da tela é filtrado pela permissão de leitura da tela que ele aponta')}
            variant="contained"
            sx={{ minHeight: 44, fontSize: '16px', mr: 1 }}
          >
            Ver primeiros passos
          </RouteActionButton>
          <IconButton aria-label="Dispensar aviso" onClick={dispensar} sx={{ width: 44, height: 44 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
    >
      <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>{titulo}</Typography>
      <Typography sx={{ fontSize: '16px' }}>
        {feitos} de {total} passos concluídos para a operação funcionar.
      </Typography>
    </Alert>
  );
};

export default OnboardingBanner;
